/**
 * GazeConfigContext.js
 * =====================
 * React context that holds the user's gaze interaction configuration for the
 * current session. State lives in React memory only — it resets to defaults
 * every time the app loads. No localStorage or sessionStorage is used so no
 * user information is persisted across visits.
 *
 * Config fields
 * ─────────────
 * selectionMode    '3-blocks' | '2-blocks'
 *   How many response options are shown per screen.
 *   '3-blocks' = current quad layout (left / right / bottom + BACK at top)
 *   '2-blocks' = simplified binary layout (left / right + BACK at top)
 *
 * confirmationMode  'long-gaze' | 'double-blink'
 *   How a highlighted selection is confirmed.
 *   'long-gaze'    = sustained gaze fires navigation immediately (no blink)
 *   'double-blink' = sustained gaze sets a pending state; double blink confirms
 *
 * speedStep  -2 | -1 | 0 | 1 | 2
 *   Controls the gaze rolling-window size fed to useGazeNavigation.
 *   0 = current default. Negative = slower (more frames required). Positive = faster.
 *
 * Derived value
 * ─────────────
 * windowSize  number  — resolved WINDOW_SIZE for useGazeNavigation, derived
 *                        from speedStep via SPEED_TO_WINDOW_SIZE.
 *
 * Usage
 * ─────
 *   // Wrap at root:
 *   <GazeConfigProvider> … <App /> … </GazeConfigProvider>
 *
 *   // Inside any component:
 *   const { config, updateConfig, windowSize } = useGazeConfig();
 */

import React, { createContext, useContext, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Speed → window size mapping
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps speedStep to WINDOW_SIZE for useGazeNavigation.
 * Selection fires when (agreeing frames / WINDOW_SIZE) >= CONFIDENCE_THRESHOLD (0.70).
 * Required agreeing frames = ceil(0.70 × WINDOW_SIZE).
 *
 *   Step -2: WINDOW_SIZE=16 → 12 frames → ~1.2 s @ 10 fps
 *   Step -1: WINDOW_SIZE=13 → 10 frames → ~1.0 s
 *   Step  0: WINDOW_SIZE=10 →  7 frames → ~0.7 s  (default — matches current tuning)
 *   Step +1: WINDOW_SIZE=6  →  5 frames → ~0.5 s
 *   Step +2: WINDOW_SIZE=2  →  2 frames → ~0.2 s  (fastest — fires after 2 frames)
 */
export const SPEED_TO_WINDOW_SIZE = {
  '-2': 16,
  '-1': 13,
   '0': 10,
   '1': 6,
   '2': 2,
};

// ─────────────────────────────────────────────────────────────────────────────
// Color scheme options
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Available color choices for background and text.
 * Users can pair any background with any text color.
 */
export const COLOR_OPTIONS = [
  { key: 'white',      label: 'White',      hex: '#ffffff' },
  { key: 'black',      label: 'Black',      hex: '#000000' },
  { key: 'red',        label: 'Red',        hex: '#D32F2F' },
  { key: 'orange',     label: 'Orange',     hex: '#E65100' },
  { key: 'sky-blue',   label: 'Sky Blue',   hex: '#29B6F6' },
  { key: 'teal-green', label: 'Teal Green', hex: '#00695C' },
  { key: 'yellow',     label: 'Yellow',     hex: '#F9A825' },
  { key: 'pink',       label: 'Pink',       hex: '#C2185B' },
];

/** Quick lookup: colorKey → hex value */
export const COLOR_MAP = Object.fromEntries(COLOR_OPTIONS.map(c => [c.key, c.hex]));

// ─────────────────────────────────────────────────────────────────────────────
// Defaults
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_CONFIG = {
  selectionMode:    '3-blocks',    // most familiar to existing users
  confirmationMode: 'long-gaze',   // simpler; double-blink is opt-in
  speedStep:        0,             // middle of the slider
  backgroundColor:  'white',       // quad page / card background
  textColor:        'black',       // quad labels, borders, title
  readAloud:        false,         // announce options via TTS before selection
  readAloudRepeat:  0,             // number of extra repeats (0 = announce once)
  voiceURI:         '',            // voiceURI of selected TTS voice; '' = system default
};

// Default iris override thresholds (before user calibration).
// upThreshold:   IVR must be below this to override model output to "up".
// downThreshold: IVR must be above this to override model output to "down".
const DEFAULT_CALIBRATION = {
  done:          false,
  upThreshold:   0.35,
  downThreshold: 0.72,
};

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const GazeConfigContext = createContext(null);

/**
 * GazeConfigProvider
 * Wrap around the Router (or around all routes) in App.js so every screen
 * can access the config via useGazeConfig().
 */
export function GazeConfigProvider({ children }) {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [calibration, setCalibration] = useState(DEFAULT_CALIBRATION);
  const [labelOverrides, setLabelOverridesState] = useState({});
  // freeResponseOptions: { [nodeId]: string[] } — custom phrases per free-response node
  const [freeResponseOptions, setFreeResponseOptions] = useState({});

  /**
   * Merge partial updates into config.
   * Example: updateConfig({ speedStep: 1 })
   */
  function updateConfig(updates) {
    setConfig(prev => ({ ...prev, ...updates }));
  }

  /**
   * Store calibrated thresholds after the CalibrationScreen finishes.
   * Example: updateCalibration({ upThreshold: 0.28, downThreshold: 0.68 })
   */
  function updateCalibration(data) {
    setCalibration(prev => ({ ...prev, ...data, done: true }));
  }

  /**
   * Set a custom label for a node for the current session.
   * Example: setLabelOverride('42', 'Call my doctor')
   */
  function setLabelOverride(nodeId, label) {
    setLabelOverridesState(prev => ({ ...prev, [String(nodeId)]: label }));
  }

  /** Append a phrase to a free-response node's option list. */
  function addFreeResponseOption(nodeId, text) {
    setFreeResponseOptions(prev => ({
      ...prev,
      [String(nodeId)]: [...(prev[String(nodeId)] ?? []), text],
    }));
  }

  /** Remove the option at the given index from a free-response node. */
  function removeFreeResponseOption(nodeId, index) {
    setFreeResponseOptions(prev => {
      const arr = [...(prev[String(nodeId)] ?? [])];
      arr.splice(index, 1);
      return { ...prev, [String(nodeId)]: arr };
    });
  }

  /** Move an option up or down in the list for a free-response node. */
  function moveFreeResponseOption(nodeId, fromIdx, toIdx) {
    setFreeResponseOptions(prev => {
      const arr = [...(prev[String(nodeId)] ?? [])];
      const [item] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, item);
      return { ...prev, [String(nodeId)]: arr };
    });
  }

  // Resolve the windowSize for the current speedStep.
  // Falls back to the step-0 value if an unexpected step is encountered.
  const windowSize = SPEED_TO_WINDOW_SIZE[String(config.speedStep)] ?? 10;

  return (
    <GazeConfigContext.Provider value={{ config, updateConfig, windowSize, calibration, updateCalibration, labelOverrides, setLabelOverride, freeResponseOptions, addFreeResponseOption, removeFreeResponseOption, moveFreeResponseOption }}>
      {children}
    </GazeConfigContext.Provider>
  );
}

/**
 * useGazeConfig
 * Hook for consuming gaze config inside any component that lives under
 * GazeConfigProvider.
 */
export function useGazeConfig() {
  const ctx = useContext(GazeConfigContext);
  if (!ctx) {
    throw new Error('useGazeConfig must be used inside <GazeConfigProvider>');
  }
  return ctx;
}
